import React, { useState, useRef, useEffect } from "react";
import { Tag, X, Upload, Loader2, PlayCircle, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "react-hot-toast";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { deleteFiles } from "services/creatorService";

/**
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Function to close the modal
 * @param {function} onSuccess - Callback after successful upload to refresh parent data
 * @param {function} uploadFn - The API service function to call
 * @param {string} titleText - The header title to display
 * @param {object} initialData - Optional: The existing project data for editing
 * @param {string} mode - "add" or "edit"
 */
const ProfileFeaturedWorkModal = ({
  open,
  onClose,
  onSuccess,
  uploadFn,
  titleText = "Featured Work",
  initialData = null,
  mode = "add",
}) => {
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState([]);

  // Files from the server (for editing)
  const [serverFiles, setServerFiles] = useState([]);
  // Newly selected files
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fileRef = useRef(null);
  const BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

  useEffect(() => {
    if (open && initialData) {
      // Initialize Edit Mode
      setTitle(initialData.title || "");
      setTags(initialData.tags || []);
      setServerFiles(initialData.items || []);
    } else if (!open) {
      // Reset logic on close
      setTitle("");
      setTags([]);
      setTagInput("");
      setSelectedFiles([]);
      setServerFiles([]);
      previews.forEach((p) => URL.revokeObjectURL(p.url));
      setPreviews([]);
      setAddTagsOpen(false);
      setLoading(false);
    }
  }, [open, initialData]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newFiles = [];
    const newPreviews = [];

    files.forEach((file) => {
      const isVideo = file.type.startsWith("video/");
      const maxSize = isVideo ? 20 * 1024 * 1024 : 10 * 1024 * 1024;

      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds limit (${isVideo ? "20MB" : "10MB"})`);
        return;
      }

      newFiles.push(file);
      newPreviews.push({
        url: URL.createObjectURL(file),
        type: file.type,
        name: file.name,
      });
    });

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index].url);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteServerFile = async (fileId) => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      const res = await deleteFiles(fileId);
      if (res && !res.error) {
        toast.success("Image removed from project");
        setServerFiles((prev) => prev.filter((f) => f.crew_files_id !== fileId));
        if (onSuccess) onSuccess(); // Refresh background data
      } else {
        toast.error("Failed to delete existing file");
      }
    } catch (err) {
      toast.error("Error occurred during deletion");
    } finally {
      setIsDeleting(false);
    }
  };

  const removeTag = (t) => {
    setTags(tags.filter((x) => x !== t));
  };

  const handleUpload = async () => {
    // UPDATED LOGIC: If editing and no NEW files are selected, just close the modal
    if (mode === "edit" && selectedFiles.length === 0) {
      onClose();
      return;
    }

    // In add mode, we require files
    if (selectedFiles.length === 0 && serverFiles.length === 0) {
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? "animate-enter" : "animate-leave"
              } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                  </div>
                </div>
                <div className="ml-3 flex-1 pr-6">
                  <p className="text-base font-bold text-gray-900">
                    No File Selected
                  </p>
                  <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                    Please select a file to upload.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ),
        { position: "bottom-right" }
      );
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // We only reach here if selectedFiles.length > 0 due to the edit check above
      selectedFiles.forEach((file) => {
        formData.append("files[]", file);
        formData.append("title", title || "Untitled Work");
        formData.append("tag", JSON.stringify(tags));
      });

      const res = await uploadFn(formData);

      if (res && !res.error) {
        toast.custom(
          (t) => (
            <div
              className={`${t.visible ? "animate-enter" : "animate-leave"
                } max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <p className="text-base font-bold text-gray-900">
                      Upload Successful
                    </p>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                      Your project has been updated successfully.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ),
          { position: "bottom-right" }
        );

        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.custom(
          (t) => (
            <div
              className={`${t.visible ? "animate-enter" : "animate-leave"
                } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <p className="text-base font-bold text-gray-900">
                      Upload Failed
                    </p>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                      {res?.error || "Upload failed. Please try again."}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ),
          { position: "bottom-right" }
        );
      }
    } catch (error) {
      console.error("Upload process error:", error);
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? "animate-enter" : "animate-leave"
              } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                  </div>
                </div>
                <div className="ml-3 flex-1 pr-6">
                  <p className="text-base font-bold text-gray-900">
                    Upload Error
                  </p>
                  <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                    An unexpected error occurred during upload. Please try
                    again.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ),
        { position: "bottom-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-[60] transition-opacity duration-300 ${open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
          }`}
        onClick={!loading ? onClose : undefined}
      />

      <div
        className={`fixed inset-0 z-[70] flex items-center justify-center p-4 transition-all duration-300 ${open
            ? "opacity-100 scale-100"
            : "opacity-0 scale-95 pointer-events-none"
          }`}
      >
        <div className="bg-white dark:bg-black-primary w-full max-w-[738px] max-h-[90vh] rounded-[24px] shadow-2xl flex flex-col overflow-hidden relative border dark:border-zinc-800">
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-zinc-800">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {titleText}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
              disabled={loading}
            >
              <X className="w-6 h-6 text-gray-500 dark:text-zinc-400" />
            </button>
          </div>

          <div className="px-8 py-6 overflow-y-auto flex-1">
            <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
              Title (Optional)
            </label>
            <Input
              placeholder="Give your work a name..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-12 rounded-xl mb-6 border-gray-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              disabled={loading}
            />

            <div
              className={`
                border-2 border-dashed rounded-2xl min-h-[220px] flex flex-col items-center justify-center
                bg-[#FCF9F6] dark:bg-zinc-900/40 transition-all group relative overflow-hidden
                ${(previews.length > 0 || serverFiles.length > 0)
                  ? "border-green-300 dark:border-green-900"
                  : "border-gray-200 dark:border-zinc-700 hover:border-gray-400 dark:hover:border-zinc-500"
                }
              `}
            >
              {(previews.length > 0 || serverFiles.length > 0) ? (
                <div className="w-full p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div
                    onClick={() => !loading && fileRef.current?.click()}
                    className="aspect-video border-2 border-dashed border-gray-300 dark:border-zinc-700 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                  >
                    <Upload size={20} />
                    <span className="text-[10px] mt-1 font-bold">Add More</span>
                  </div>

                  {/* Existing Server Files */}
                  {serverFiles.map((file) => (
                    <div key={file.crew_files_id} className="relative aspect-video rounded-xl overflow-hidden group/item shadow-sm border border-green-200 dark:border-green-900/30">
                      <img
                        src={`${BASE_URL}${file.file_path}`}
                        className="w-full h-full object-cover"
                        alt="Existing"
                      />
                      <div className="absolute top-1 left-1 bg-green-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                        Live
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteServerFile(file.crew_files_id); }}
                        disabled={isDeleting}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity"
                      >
                        {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </div>
                  ))}

                  {/* New Previews */}
                  {previews.map((prev, idx) => (
                    <div key={idx} className="relative aspect-video rounded-xl overflow-hidden group/item shadow-sm border dark:border-zinc-700">
                      {prev.type.startsWith("video/") ? (
                        <div className="w-full h-full bg-black flex flex-col items-center justify-center">
                          <PlayCircle className="w-8 h-8 text-white opacity-80" />
                        </div>
                      ) : (
                        <img
                          src={prev.url}
                          className="w-full h-full object-cover"
                          alt="Preview"
                        />
                      )}
                      <div className="absolute top-1 left-1 bg-blue-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                        New
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div onClick={() => !loading && fileRef.current?.click()} className="text-center px-6 py-10 cursor-pointer">
                  <div className="w-14 h-14 bg-white dark:bg-zinc-800 rounded-full shadow-sm flex items-center justify-center mx-auto mb-4 border dark:border-zinc-700">
                    <Upload className="w-6 h-6 text-gray-600 dark:text-zinc-400" />
                  </div>
                  <h4 className="font-bold text-gray-900 dark:text-white text-lg">
                    Upload files or browse
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-zinc-500 mt-1">
                    Images (png, jpg) or Videos (mp4). Max 20MB.
                  </p>
                </div>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/mp4,video/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            <div className="mt-8">
              <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-3">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 bg-gray-100 dark:bg-zinc-800 border border-transparent dark:border-zinc-700 rounded-full flex items-center gap-2 text-sm dark:text-zinc-300"
                  >
                    #{t}
                    <button
                      onClick={() => removeTag(t)}
                      disabled={loading}
                      className="hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="px-8 py-6 border-t border-gray-100 dark:border-zinc-800 flex flex-wrap gap-2 items-center justify-between bg-gray-50/50 dark:bg-zinc-900/50">
            <button
              onClick={() => setAddTagsOpen(true)}
              className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-zinc-400 hover:text-black dark:hover:text-white"
              disabled={loading}
            >
              <Tag className="w-5 h-5" /> Manage Tags
            </button>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="rounded-full px-8 h-11 font-bold dark:border-zinc-700 dark:text-white dark:hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={loading}
                className="rounded-full px-10 h-11 font-bold bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-zinc-200 flex items-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Processing..." : mode === "edit" ? "Update Project" : "Confirm & Publish"}
              </Button>
            </div>
          </div>

          {addTagsOpen && (
            <div className="absolute inset-0 z-[80] flex items-center justify-center p-6">
              <div
                className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
                onClick={() => setAddTagsOpen(false)}
              />
              <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-2xl p-8 z-[90] border dark:border-zinc-800">
                <h2 className="text-xl font-bold mb-4 dark:text-white">
                  Add Tags
                </h2>
                <Input
                  placeholder="Type a tag and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  className="h-12 rounded-xl dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const trimmed = tagInput.trim();
                      if (trimmed && !tags.includes(trimmed))
                        setTags([...tags, trimmed]);
                      setTagInput("");
                    }
                  }}
                />
                <div className="flex flex-wrap gap-2 mt-4">
                  {tags.map((t) => (
                    <div
                      key={t}
                      className="px-3 py-1 bg-black dark:bg-white text-white dark:text-black rounded-full flex items-center gap-2 text-xs"
                    >
                      {t}{" "}
                      <button
                        onClick={() => setTags(tags.filter((tag) => tag !== t))}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => setAddTagsOpen(false)}
                  className="w-full mt-6 rounded-xl bg-black dark:bg-white text-white dark:text-black h-12"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileFeaturedWorkModal;
